import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axios';
import OrderDetails from '../OrderDetails';

// ============================================
// Component: CompanyOrdersList
// Purpose: Display all orders for a specific company
// Features:
// - Filter by payment status (pending, partial, paid)
// - Filter by order status (delivered, processing, etc.)
// - Summary stats
// - Link to order details
// ============================================

const CompanyOrdersList = ({ companyId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [companyId]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/orders/company/${companyId}`);
      // Filter out cancelled orders - they shouldn't be displayed
      const activeOrders = (response.data.orders || []).filter(order => order.status !== 'cancelled');
      setOrders(activeOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-red-100 text-red-700',
      partial: 'bg-yellow-100 text-yellow-700',
      paid: 'bg-green-100 text-green-700'
    };

    const statusLabels = {
      pending: 'ממתין לתשלום',
      partial: 'שולם חלקית',
      paid: 'שולם'
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[status]}`}>
        {statusLabels[status]}
      </span>
    );
  };

  const getOrderStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-gray-100 text-gray-700',
      confirmed: 'bg-blue-100 text-blue-700',
      processing: 'bg-purple-100 text-purple-700',
      shipped: 'bg-indigo-100 text-indigo-700',
      delivered: 'bg-green-100 text-green-700'
    };

    const statusLabels = {
      pending: 'ממתין',
      confirmed: 'אושר',
      processing: 'בטיפול',
      shipped: 'נשלח',
      delivered: 'נמסר'
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[status]}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesPaymentStatus = paymentStatusFilter === 'all' || order.payment_status === paymentStatusFilter;
    const matchesOrderStatus = orderStatusFilter === 'all' || order.status === orderStatusFilter;
    return matchesPaymentStatus && matchesOrderStatus;
  });

  // Calculate stats
  const stats = {
    total: orders.length,
    pending_payment: orders.filter(o => o.payment_status === 'pending').length,
    partial_payment: orders.filter(o => o.payment_status === 'partial').length,
    paid: orders.filter(o => o.payment_status === 'paid').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    // total_amount already includes VAT - do NOT multiply again
    total_amount: filteredOrders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0),
    total_paid: filteredOrders.reduce((sum, o) => sum + parseFloat(o.paid_amount || 0), 0),
    total_unpaid: filteredOrders.reduce((sum, o) => sum + (parseFloat(o.total_amount || 0) - parseFloat(o.paid_amount || 0)), 0)
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">אין הזמנות</h3>
        <p className="mt-1 text-sm text-gray-500">לא נמצאו הזמנות לחברה זו</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-600 mb-1">סה״כ הזמנות</div>
          <div className="text-xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
          <div className="text-xs text-blue-700 mb-1">סה״כ סכום הזמנות (כולל מע"מ)</div>
          <div className="text-lg font-bold text-blue-900">{formatCurrency(stats.total_amount)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-600 mb-1">ממתין לתשלום</div>
          <div className="text-xl font-bold text-red-600">{stats.pending_payment}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-600 mb-1">שולם חלקית</div>
          <div className="text-xl font-bold text-yellow-600">{stats.partial_payment}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-600 mb-1">שולם במלואו</div>
          <div className="text-xl font-bold text-green-600">{stats.paid}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">סטטוס תשלום</label>
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">הכל ({orders.length})</option>
            <option value="pending">ממתין לתשלום ({stats.pending_payment})</option>
            <option value="partial">שולם חלקית ({stats.partial_payment})</option>
            <option value="paid">שולם ({stats.paid})</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">סטטוס הזמנה</label>
          <select
            value={orderStatusFilter}
            onChange={(e) => setOrderStatusFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">הכל</option>
            <option value="pending">ממתין</option>
            <option value="confirmed">אושר</option>
            <option value="processing">בטיפול</option>
            <option value="shipped">נשלח</option>
            <option value="delivered">נמסר</option>
          </select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  מספר הזמנה
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  תאריך
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  סכום כולל (כולל מע"מ)
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  סכום ששולם
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  יתרה
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  סטטוס משלוח
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  סטטוס תשלום
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  פעולות
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-gray-500">
                    אין הזמנות התואמות את הפילטרים
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => {
                  // total_amount already includes VAT - use directly
                  const orderTotalWithVat = parseFloat(order.total_amount || 0);
                  const unpaid = orderTotalWithVat - parseFloat(order.paid_amount || 0);
                  return (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {order.order_number}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">
                        {formatCurrency(orderTotalWithVat)}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-green-600">
                        {formatCurrency(order.paid_amount || 0)}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-red-600">
                        {formatCurrency(unpaid)}
                      </td>
                      <td className="px-4 py-3">
                        {getOrderStatusBadge(order.status)}
                      </td>
                      <td className="px-4 py-3">
                        {getPaymentStatusBadge(order.payment_status)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderDetails(true);
                          }}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          צפה בהזמנה
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filteredOrders.length > 0 && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="2" className="px-4 py-3 text-sm font-bold text-gray-700">
                    סה״כ ({filteredOrders.length} הזמנות)
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900">
                    {formatCurrency(stats.total_amount)}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-green-600">
                    {formatCurrency(stats.total_paid)}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-red-600">
                    {formatCurrency(stats.total_unpaid)}
                  </td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Order Details Modal - inline display without navigation */}
      <OrderDetails
        isOpen={showOrderDetails}
        onClose={() => {
          setShowOrderDetails(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
        onOrderUpdated={() => {
          fetchOrders();
        }}
      />
    </div>
  );
};

export default CompanyOrdersList;

